<div class="mt-4 bg-white dark:bg-slate-800 rounded-xl lg:rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden">
  <!-- Header -->
  <button (click)="toggleExpand()"
    class="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-all cursor-pointer group">
    <div class="flex items-center gap-3">
      <div class="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 flex items-center justify-center group-hover:scale-105 transition-transform">
        <app-icon-v2 name="Activity" [size]="16"></app-icon-v2>
      </div>
      <span class="text-[14px] font-bold text-slate-800 dark:text-slate-100 tracking-tight">Audit Sessions</span>
      @if(activity()?.reviewTimes?.length) {
        <span class="px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-700 text-[11px] font-bold text-slate-500 dark:text-slate-400">
          {{ activity()!.reviewTimes.length }}
        </span>
      }
    </div>
    <div class="w-6 h-6 rounded-full flex items-center justify-center bg-slate-50 dark:bg-slate-800 text-slate-400 group-hover:bg-indigo-50 dark:group-hover:bg-white/10 group-hover:text-indigo-600 dark:group-hover:text-white transition-colors">
      <app-icon-v2 [name]="isExpanded() ? 'ChevronUp' : 'ChevronDown'" [size]="14"></app-icon-v2>
    </div>
  </button>

  @if (isExpanded()) {
    <div class="px-5 pb-5 border-t border-slate-50 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-900/20">
      @if (isLoading()) {
        <div class="flex items-center justify-center py-8">
          <div class="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      } @else if (hasError()) {
        <div class="flex flex-col items-center justify-center py-8 text-red-500">
          <app-icon-v2 name="AlertCircle" [size]="24" customClass="mb-2 opacity-50"></app-icon-v2>
          <p class="text-xs font-medium">Failed to load activity.</p>
        </div>
      } @else {
        @if (activity(); as a) {

          <!-- Per-reviewer active time -->
          @if (a.reviewTimes.length > 0) {
            <div class="mt-4 space-y-3">
              <p class="text-[11px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500 mb-1">Session History</p>
              @for (rt of a.reviewTimes; track rt.id) {
                <div class="flex flex-col bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700/60 shadow-sm overflow-hidden transition-all duration-200 hover:border-indigo-300 dark:hover:border-line-strong hover:shadow-md">
                  
                  <!-- Session Header (Clickable) -->
                  <button (click)="toggleSession(rt.sessionId)" class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 w-full text-left cursor-pointer group/session relative bg-transparent hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors">
                    
                    <!-- Left Status Bar -->
                    <div class="absolute left-0 top-0 bottom-0 w-1 transition-colors" [ngClass]="rt.isVerified ? 'bg-green-500' : 'bg-indigo-500'"></div>

                    <!-- User Info -->
                    <div class="flex items-center gap-3 min-w-0 pl-1.5">
                      <div class="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 flex items-center justify-center text-slate-500 dark:text-slate-300 shrink-0">
                        <app-icon-v2 name="User" [size]="16"></app-icon-v2>
                      </div>
                      <div class="flex flex-col min-w-0">
                        <div class="flex items-center gap-2">
                          <span class="text-[13px] font-bold text-slate-800 dark:text-slate-100 truncate">
                            {{ rt.userName || ('Reviewer #' + rt.userPid) }}
                          </span>
                          @if (rt.isVerified) {
                            <span class="flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border border-green-100 dark:border-green-800/30">
                              <app-icon-v2 name="circle-check" [size]="10"></app-icon-v2> Verified
                            </span>
                          }
                        </div>
                        <div class="flex items-center gap-2 text-[11px] font-medium text-slate-500 dark:text-slate-400 mt-0.5">
                          @if (rt.firstOpenedAt) {
                            <span class="flex items-center gap-1"><app-icon-v2 name="Calendar" [size]="10"></app-icon-v2> {{ rt.firstOpenedAt | date:'MMM d, yyyy':'PDT' }}</span>
                            <span class="w-1 h-1 rounded-full bg-slate-300 dark:bg-slate-600"></span>
                            <span>{{ rt.firstOpenedAt | date:'h:mm a':'PDT' }}</span>
                          }
                        </div>
                      </div>
                    </div>

                    <!-- Duration & Toggle -->
                    <div class="flex items-center justify-between sm:justify-end w-full sm:w-auto gap-4 pl-12 sm:pl-0">
                      <div class="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-800/30">
                        <app-icon-v2 name="Clock" [size]="12"></app-icon-v2>
                        <span class="text-[12px] font-bold tabular-nums">{{ formatDuration(rt.activeMs) }}</span>
                      </div>
                      
                      <div class="w-6 h-6 rounded-full flex items-center justify-center text-slate-400 group-hover/session:bg-slate-100 dark:group-hover/session:bg-slate-700 group-hover/session:text-indigo-500 transition-colors">
                        <app-icon-v2 [name]="isSessionExpanded(rt.sessionId) ? 'ChevronUp' : 'ChevronDown'" [size]="14"></app-icon-v2>
                      </div>
                    </div>
                  </button>
                  
                  <!-- Expanded Timeline -->
                  @if (isSessionExpanded(rt.sessionId)) {
                    <div class="px-5 py-4 border-t border-slate-100 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-900/30">
                      @if (eventsBySession()[rt.sessionId]?.length) {
                        <div class="relative pl-3 space-y-4 before:absolute before:inset-y-2 before:left-[15px] before:w-px before:bg-slate-200 dark:before:bg-slate-700">
                          @for (ev of eventsBySession()[rt.sessionId]; track ev.id) {
                            <div class="relative flex items-center gap-3 group/event">
                              <div class="w-2 h-2 rounded-full ring-4 ring-slate-50 dark:ring-slate-900 z-10 transition-transform group-hover/event:scale-125" [class]="eventDotClass(ev.eventType)"></div>
                              <div class="flex-1 flex flex-wrap items-center justify-between gap-2 bg-white dark:bg-slate-800 px-3 py-2 rounded-lg border border-slate-100 dark:border-slate-700/50 shadow-sm">
                                <span class="text-[12px] font-semibold text-slate-700 dark:text-slate-200">{{ eventLabel(ev.eventType) }}</span>
                                <span class="text-[11px] font-medium text-slate-400 tabular-nums">{{ ev.occurredAt | date:'h:mm:ss a':'PDT' }}</span>
                              </div>
                            </div>
                          }
                        </div>
                      } @else {
                        <div class="flex items-center gap-2 text-slate-400 justify-center py-2">
                          <app-icon-v2 name="Info" [size]="14"></app-icon-v2>
                          <p class="text-[12px] font-medium">No activity recorded for this session.</p>
                        </div>
                      }
                    </div>
                  }
                </div>
              }
            </div>
          }

          <!-- Event timeline for older unstructured events -->
          @if (unassignedEvents().length > 0) {
            <div class="mt-6">
              <p class="text-[11px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500 mb-3">Legacy Activity Timeline</p>
              <div class="relative pl-3 space-y-4 before:absolute before:inset-y-2 before:left-[15px] before:w-px before:bg-slate-200 dark:before:bg-slate-700">
                @for (ev of unassignedEvents(); track ev.id) {
                  <div class="relative flex items-center gap-3 group/event">
                    <div class="w-2 h-2 rounded-full ring-4 ring-slate-50 dark:ring-slate-900 z-10 transition-transform group-hover/event:scale-125" [class]="eventDotClass(ev.eventType)"></div>
                    <div class="flex-1 flex flex-wrap items-center justify-between gap-2 bg-white dark:bg-slate-800 px-3 py-2 rounded-lg border border-slate-100 dark:border-slate-700/50 shadow-sm">
                      <div class="flex items-center gap-2">
                        <span class="text-[12px] font-semibold text-slate-700 dark:text-slate-200">{{ eventLabel(ev.eventType) }}</span>
                        <span class="text-[10px] font-medium text-slate-400 px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-slate-700">{{ ev.userName || ('User #' + ev.userPid) }}</span>
                      </div>
                      <span class="text-[11px] font-medium text-slate-400 tabular-nums">{{ ev.occurredAt | date:'MMM d, h:mm a':'PDT' }}</span>
                    </div>
                  </div>
                }
              </div>
            </div>
          }

          @if (a.reviewTimes.length === 0 && unassignedEvents().length === 0) {
            <div class="flex flex-col items-center justify-center py-10 text-center opacity-60">
              <app-icon-v2 name="History" [size]="32" customClass="mb-3 text-slate-400"></app-icon-v2>
              <p class="text-[13px] font-semibold text-slate-600 dark:text-slate-300 mb-1">No Completed Sessions</p>
              <p class="text-[11px] font-medium text-slate-400 max-w-[200px]">Past review activity will appear here once you close the audit.</p>
            </div>
          }

        }
      }
    </div>
  }
</div>
